import { getInstanceId } from '@va/dashboard/selectors/app';
import { getTrafficFilter } from '@va/dashboard/selectors/ui';
import { useTranslate } from '@va/localization';
import { getHiddenDataset } from '@va/ui/design-system';
import { toQueryString } from '@va/util/helpers';
import { useFetchData } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { chartColor } from './TrafficChartCard';

const useVisitorsResource = (time: string) => {
  const websiteId = useSelector(getInstanceId);
  const trafficFilter = useSelector(getTrafficFilter);
  const translate = useTranslate();

  const mapper = useCallback(
    (data: Response) => {
      const datasetData = data.map((item) => item.count);
      const labels = data.map((item) => {
        const label = item.title;
        if (time === 'hourly') return `${label}:00`;
        return translate(`panels.trafficCharts.label.${label}`);
      });
      return {
        labels: labels,
        datasets: [
          {
            label: 'Visitors',
            data: datasetData,
            backgroundColor: getDatasetColors(datasetData),
          },
          { ...getHiddenDataset(datasetData) },
        ],
      };
    },
    [time, translate],
  );

  const url = useMemo(() => {
    let u = `/websites/${websiteId}/visitors/${time}`;
    if (trafficFilter.specificYear) {
      u += `?${toQueryString({ specificYear: trafficFilter.specificYear })}`;
    }
    return u;
  }, [time, trafficFilter.specificYear, websiteId]);

  return useFetchData<ReturnType<typeof mapper>>(
    url,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
    },
    mapper,
  );
};

const useVisitsResource = (time: string) => {
  const websiteId = useSelector(getInstanceId);
  const trafficFilter = useSelector(getTrafficFilter);
  const translate = useTranslate();

  const mapper = useCallback(
    ({ payload: data }: { payload: Response }) => {
      const datasetData = data.map((item) => item.count);
      const labels = data.map((item) => {
        const label = item.title;
        if (time === 'hourly') return `${label}:00`;
        return translate(`panels.trafficCharts.label.${label}`);
      });
      return {
        labels: labels,
        datasets: [
          {
            label: 'Visits',
            data: datasetData,
            backgroundColor: getDatasetColors(datasetData),
          },
          { ...getHiddenDataset(datasetData) },
        ],
      };
    },
    [time, translate],
  );

  const url = useMemo(() => {
    let u = `/v2/websites/${websiteId}/visits/${time}`;
    if (trafficFilter.specificYear) {
      u += `?${toQueryString({ specificYear: trafficFilter.specificYear })}`;
    }
    return u;
  }, [time, trafficFilter.specificYear, websiteId]);

  return useFetchData<ReturnType<typeof mapper>>(
    url,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
    },
    mapper,
  );
};

export const useNumberOfVisitorsByHour = () => {
  return useVisitorsResource('hourly');
};

export const useNumberOfVisitorsByMonth = () => {
  return useVisitorsResource('monthly');
};

export const useNumberOfVisitorByDay = () => {
  return useVisitorsResource('daily');
};

export const useNumberOfVisitsByDay = () => {
  return useVisitsResource('daily');
};

export const useNumberOfVisitsByHour = () => {
  return useVisitsResource('hourly');
};

export const useNumberOfVisitsByMonth = () => {
  return useVisitsResource('monthly');
};

type Response = { title: string; count: number }[];

function getDatasetColors(data: number[]) {
  const max = Math.max(...data);
  return data.map((count) => {
    const p = count / max;
    if (p > 0.66) return chartColor.high;
    if (p > 0.33) return chartColor.medium;
    return chartColor.low;
  });
}
